export const SYRINGE_VOLUME_ML = 1;
export const MAX_COMPOUNDS = 10;
const U100_UNITS_PER_ML = 100;
const EPSILON = 1e-10;

const NUMBER_FORMAT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const UNITS_FORMAT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

export function emptyCompound(id) {
  return { id, quantity: null, unit: "mg", doseUnit: "mg" };
}

export function emptyMeasureState() {
  return {
    entryMode: "amount",
    volumeMl: null,
    compounds: [emptyCompound("compound-1")],
    target: { kind: "total", value: null },
    doseUnit: "mg",
  };
}

export function parseMeasureState(raw) {
  try {
    const saved = JSON.parse(raw);
    const state = saved?.state;
    const validNumber = (value) => value === null || positive(value);
    const validUnit = (unit) => ["mg", "mcg"].includes(unit);
    if (
      saved?.version !== 1 ||
      !state ||
      !["amount", "strength"].includes(state.entryMode) ||
      !validNumber(state.volumeMl) ||
      !validUnit(state.doseUnit) ||
      !Array.isArray(state.compounds) ||
      state.compounds.length < 1 ||
      state.compounds.length > MAX_COMPOUNDS ||
      !state.compounds.every(
        (compound) =>
          compound &&
          typeof compound.id === "string" &&
          /^compound-\d+$/.test(compound.id) &&
          validNumber(compound.quantity) &&
          validUnit(compound.unit) &&
          validUnit(compound.doseUnit)
      ) ||
      new Set(state.compounds.map((compound) => compound.id)).size !== state.compounds.length ||
      !state.target ||
      !["volume", "total", "component"].includes(state.target.kind) ||
      !validNumber(state.target.value) ||
      (state.target.kind === "component" && !state.compounds.some((compound) => compound.id === state.target.id))
    )
      return null;

    return state;
  } catch {
    return null;
  }
}

export function measureNumber(raw) {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(trimmed)) return null;

  const value = Number(trimmed);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function fromDisplay(value, unit) {
  if (value === null) return null;
  if (unit === "mcg") return value / 1000;
  if (unit === "units") return value / U100_UNITS_PER_ML;
  return value;
}

export function toDisplay(value, unit) {
  if (value === null) return null;
  if (unit === "mcg") return value * 1000;
  if (unit === "units") return value * U100_UNITS_PER_ML;
  return value;
}

export function calculateMeasure(state) {
  const quantitiesReady =
    state.compounds.length > 0 && state.compounds.every((compound) => positive(compound.quantity));
  const volumeReady = positive(state.volumeMl);
  const supplyReady = quantitiesReady && (state.entryMode === "strength" || volumeReady);

  const concentrations = supplyReady
    ? state.compounds.map((compound) =>
        state.entryMode === "strength" ? compound.quantity : compound.quantity / state.volumeMl
      )
    : [];
  const concentration = supplyReady ? concentrations.reduce((sum, value) => sum + value, 0) : null;
  const totalQuantity = quantitiesReady ? state.compounds.reduce((sum, compound) => sum + compound.quantity, 0) : null;
  const totalInVial =
    state.entryMode === "amount"
      ? totalQuantity
      : volumeReady && totalQuantity !== null
        ? totalQuantity * state.volumeMl
        : null;

  let volumeMl = null;
  if (supplyReady && positive(state.target.value)) {
    if (state.target.kind === "volume") {
      volumeMl = state.target.value;
    } else if (state.target.kind === "total" && concentration !== null) {
      volumeMl = state.target.value / concentration;
    } else if (state.target.kind === "component") {
      const index = state.compounds.findIndex((compound) => compound.id === state.target.id);
      if (index >= 0) volumeMl = state.target.value / concentrations[index];
    }
  }

  const doses = positive(volumeMl) ? concentrations.map((value) => value * volumeMl) : [];
  const ready = positive(volumeMl) && doses.every(positive);
  if (!ready) volumeMl = null;

  return {
    ready,
    supplyReady,
    concentration,
    concentrations: supplyReady ? concentrations : [],
    totalInVial: totalInVial !== null && Number.isFinite(totalInVial) ? totalInVial : null,
    volumeMl,
    doses: ready ? doses : [],
    totalDose: ready ? doses.reduce((sum, value) => sum + value, 0) : null,
    fullDoses: ready && volumeReady ? Math.floor(state.volumeMl / volumeMl + EPSILON) : null,
    syringeUnits: ready ? volumeMl * U100_UNITS_PER_ML : 0,
    exceedsSyringe: volumeMl !== null && volumeMl > SYRINGE_VOLUME_ML + EPSILON,
    exceedsVial: volumeMl !== null && volumeReady && volumeMl > state.volumeMl + EPSILON,
  };
}

export function changeMeasureMode(state, entryMode) {
  if (entryMode === state.entryMode) return state;
  if (state.compounds.some((compound) => compound.quantity !== null) && !positive(state.volumeMl)) return null;

  return {
    ...state,
    entryMode,
    compounds: state.compounds.map((compound) => ({
      ...compound,
      quantity:
        compound.quantity === null
          ? null
          : entryMode === "strength"
            ? compound.quantity / state.volumeMl
            : compound.quantity * state.volumeMl,
    })),
  };
}

export function editMeasure(state, field, raw, unit) {
  const value = fromDisplay(measureNumber(raw), unit);

  if (field.kind === "volume") return { ...state, volumeMl: value };
  if (field.kind === "draw") return { ...state, target: { kind: "volume", value } };
  if (field.kind === "total") return { ...state, doseUnit: unit, target: { kind: "total", value } };
  if (field.kind === "component") {
    return {
      ...state,
      target: { kind: "component", id: field.id, value },
      compounds: state.compounds.map((compound) =>
        compound.id === field.id ? { ...compound, doseUnit: unit } : compound
      ),
    };
  }
  if (field.kind === "quantity") {
    return {
      ...state,
      compounds: state.compounds.map((compound) =>
        compound.id === field.id ? { ...compound, quantity: value, unit } : compound
      ),
    };
  }

  return state;
}

export function addMeasureCompound(state) {
  if (state.compounds.length >= MAX_COMPOUNDS) return state;

  let index = state.compounds.length + 1;
  while (state.compounds.some((compound) => compound.id === `compound-${index}`)) index += 1;

  return { ...state, compounds: [...state.compounds, emptyCompound(`compound-${index}`)] };
}

export function removeMeasureCompound(state, id) {
  if (state.compounds.length <= 1) return state;

  const result = calculateMeasure(state);

  return {
    ...state,
    compounds: state.compounds.filter((compound) => compound.id !== id),
    target:
      state.target.kind === "component" && state.target.id === id
        ? { kind: "volume", value: result.volumeMl }
        : state.target,
  };
}

export function formatNumber(value) {
  return NUMBER_FORMAT.format(value);
}

export function formatVolume(value) {
  return value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

export function formatUnits(value) {
  return UNITS_FORMAT.format(value);
}

export function formatInput(value) {
  if (value === null || !Number.isFinite(value)) return "";
  return value.toLocaleString("en-US", { maximumSignificantDigits: 12, useGrouping: false });
}

export function measureCompoundName(compound, index) {
  return compound?.name || `Compound ${index + 1}`;
}

function positive(value) {
  return value !== null && Number.isFinite(value) && value > 0;
}
